import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Tv } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';

export default function Login() {
  const { signInWithPassword, signInWithOtp, signInWithGoogle, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'password' | 'magic'>('password');
  const [submitting, setSubmitting] = useState(false);
  const [showEmailFlow, setShowEmailFlow] = useState(false);

  if (session) {
    // Prefer ?next= (from invite/share links) over ProtectedRoute's
    // saved-state redirect, since an explicit query param always
    // reflects the user's most recent intent.
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    const fallback = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';
    // Guard against open-redirect: only allow same-origin paths.
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    navigate(safeNext ?? fallback, { replace: true });
    return null;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'password') {
        const { error } = await signInWithPassword(email, password);
        if (error) toast.error(error);
        else toast.success('Signed in');
      } else {
        const { error } = await signInWithOtp(email);
        if (error) toast.error(error);
        else toast.success('Magic link sent — check your email');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setSubmitting(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) toast.error(error);
      // No success toast — the page navigates away to Google's OAuth flow.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <Tv className="w-6 h-6 text-indigo-600" />
          <span className="text-lg font-semibold text-gray-900">GASWEB Channel</span>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-1">Sign in or sign up</h1>
        <p className="text-sm text-gray-500 mb-6">
          New here? The same Google button below creates your account.
        </p>

        {/* Primary CTA — Google OAuth handles both sign-in AND sign-up. */}
        <button
          type="button"
          onClick={onGoogle}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          <GoogleLogo />
          Continue with Google
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-gray-400">
          <div className="flex-1 border-t border-gray-200" />
          <span>or use email</span>
          <div className="flex-1 border-t border-gray-200" />
        </div>

        {!showEmailFlow ? (
          <button
            type="button"
            onClick={() => setShowEmailFlow(true)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Sign in with email + password
          </button>
        ) : (
          <>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="you@example.com"
                />
              </div>

              {mode === 'password' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-indigo-600 text-white py-2 text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitting ? 'Please wait…' : mode === 'password' ? 'Sign in' : 'Send magic link'}
              </button>
            </form>

            <div className="mt-4 flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setMode(mode === 'password' ? 'magic' : 'password')}
                className="text-indigo-600 hover:underline"
              >
                {mode === 'password' ? 'Use magic link instead' : 'Use password instead'}
              </button>
              <button
                type="button"
                onClick={() => setShowEmailFlow(false)}
                className="text-gray-500 hover:underline"
              >
                Hide
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Google's official "G" logo SVG. Inlined so we don't pull a new icon
 * dep just for one button.
 */
function GoogleLogo() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
    </svg>
  );
}
